import React, { useEffect, useState } from "react";
import {
  LuArrowUp,
  LuArrowUpRight,
  LuInfo,
  LuUserPlus,
} from "react-icons/lu";

export default function ScrollJoinBar() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hero = document.querySelector(".tribePolaroidHero");
    if (!hero) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      const handleScroll = () => {
        setIsVisible(window.scrollY > window.innerHeight * 0.8);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();

      return () => window.removeEventListener("scroll", handleScroll);
    }

    let heroVisible = true;
    let footerVisible = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === hero) heroVisible = entry.isIntersecting;
          if (entry.target.classList.contains("desktopLandingFooter")) {
            footerVisible = entry.isIntersecting;
          }
        });

        setIsVisible(!heroVisible && !footerVisible);
      },
      { threshold: 0 }
    );

    observer.observe(hero);

    const footer = document.querySelector(".desktopLandingFooter");
    if (footer) observer.observe(footer);

    return () => observer.disconnect();
  }, []);

  const scrollToTop = () => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.scrollTo({
      top: 0,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        .landingScrollCta {
          position: fixed;
          z-index: 50;
          left: 50%;
          bottom: calc(10px + env(safe-area-inset-bottom, 0px));
          transform: translateX(-50%);
          width: calc(100% - 20px);
          max-width: 740px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 12px;
          background: rgba(255,255,255,0.96);
          border: 1px solid rgba(15,23,42,0.08);
          border-radius: 12px;
          box-shadow: 0 14px 38px rgba(15,23,42,0.14);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
          animation: landingScrollCtaIn 220ms ease both;
        }

        .landingScrollCta__icon {
          width: 30px;
          height: 30px;
          flex: 0 0 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          color: #0b0b0b;
        }

        .landingScrollCta__copy {
          flex: 1 1 0;
          min-width: 0;
        }

        .landingScrollCta__heading {
          margin: 0;
          color: #0b0b0b;
          font-size: 13.5px;
          font-weight: 900;
          line-height: 1.05;
          letter-spacing: -0.035em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .landingScrollCta__subheading {
          display: flex;
          align-items: center;
          gap: 5px;
          margin: 5px 0 0;
          color: #6b7280;
          font-size: 12px;
          font-weight: 650;
          line-height: 1.18;
          letter-spacing: -0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .landingScrollCta__button {
          width: 140px;
          min-width: 140px;
          height: 42px;
          padding: 0 13px;
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          flex: 0 0 140px;
          border: 1px solid #fbe67a;
          border-radius: 8px;
          background: #fbe67a;
          color: #171717;
          font-family: inherit;
          font-size: 12px;
          font-weight: 875;
          letter-spacing: -0.015em;
          line-height: 1.2;
          text-decoration: none;
          transition: background 160ms ease, border-color 160ms ease;
        }

        .landingScrollCta__button:hover {
          background: #f5dc59;
          border-color: #f5dc59;
        }

        .landingScrollTop {
          position: fixed;
          z-index: 49;
          left: 50%;
          bottom: calc(94px + env(safe-area-inset-bottom, 0px));
          transform: translateX(-50%);
          height: 36px;
          padding: 0 13px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border: 1px solid rgba(15,23,42,0.1);
          border-radius: 999px;
          background: rgba(255,255,255,0.96);
          color: #343c54;
          box-shadow: 0 8px 24px rgba(15,23,42,0.12);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          font-family: inherit;
          font-size: 12px;
          font-weight: 750;
          cursor: pointer;
          transition: background 160ms ease, transform 160ms ease;
        }

        .landingScrollTop:hover {
          background: #fff;
          transform: translateX(-50%) translateY(-1px);
        }

        .landingScrollCta :is(a, button):focus-visible,
        .landingScrollTop:focus-visible {
          outline: 2px solid #343c54;
          outline-offset: 4px;
        }

        @keyframes landingScrollCtaIn {
          from {
            opacity: 0;
            translate: 0 8px;
          }
          to {
            opacity: 1;
            translate: 0 0;
          }
        }

        @media (max-width: 360px) {
          .landingScrollCta {
            gap: 8px;
          }
        }

        @media (min-width: 441px) {
          .landingScrollCta,
          .landingScrollTop {
            display: none !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .landingScrollCta__button,
          .landingScrollTop {
            transition: none;
          }

          .landingScrollCta {
            animation: none;
          }
        }
      `}</style>

      <button
        type="button"
        className="landingScrollTop"
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <LuArrowUp size={15} aria-hidden="true" />
        Back to top
      </button>

      <aside className="landingScrollCta" aria-label="Join Tribe">
        <span className="landingScrollCta__icon" aria-hidden="true">
          <LuUserPlus size={19} strokeWidth={2.2} />
        </span>

        <div className="landingScrollCta__copy">
          <p className="landingScrollCta__heading">Become a member</p>
          <p className="landingScrollCta__subheading">
            Free to join · Invite-only
          </p>
        </div>

        <a className="landingScrollCta__button" href="/register">
          Get Started
          <LuArrowUpRight size={16} aria-hidden="true" />
        </a>
      </aside>
    </>
  );
}